import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { PoReceiptCoverage, PoReceiptCoverageRequest } from '../models/po-receipt/po-receipt';
import { PoReceiptProxyService } from '../proxies/po-receipt.proxy.service';

/**
 * "Would paying these take a PO past what was received?" (#5662), kept current as the amounts on a payment
 * screen change. One batch call per settled edit — debounced, and cancelled by the next — never one per row.
 *
 * The rule itself is API-side (`po-receipt/coverage/by-documents`), so this screen and the payment post
 * cannot disagree. A failed check shows no warning rather than blocking anything: it is advice, and the
 * post is where the rule is enforced.
 */
export function watchReceivedCoverage(
  requests$: Observable<PoReceiptCoverageRequest[]>,
  proxy: PoReceiptProxyService,
): Observable<Map<string, PoReceiptCoverage>> {
  return requests$.pipe(
    map((requests) => requests.filter((r) => r.documentReference !== '' && r.amount > 0)),
    distinctUntilChanged((a, b) => requestKey(a) === requestKey(b)),
    debounceTime(400),
    switchMap((requests) =>
      requests.length === 0
        ? of(new Map<string, PoReceiptCoverage>())
        : proxy.getCoverage(requests).pipe(
            map((response) => new Map((response.data ?? []).map((c) => [c.documentReference, c] as const))),
            catchError(() => of(new Map<string, PoReceiptCoverage>())),
          ),
    ),
  );
}

/** One line per PO that is over, e.g. "PO-12: received 6,000.00 · paid 0.00 · paying 50,000.00 — 44,000.00 over". */
export function receivedCoverageTooltip(
  coverage: PoReceiptCoverage | undefined,
  translate: TranslateService,
  currencyLabels: Record<number, string>,
): string {
  if (coverage === undefined || !coverage.exceedsReceived) return '';

  const lines = coverage.purchaseOrders
    .filter((po) => po.checked && po.shortfall > 0)
    .map((po) => {
      const currency = po.currency === null ? '' : ` ${currencyLabels[po.currency] ?? ''}`;
      const params = {
        po: po.purchaseOrderNumber ?? '',
        received: money(po.netReceived) + currency,
        paid: money(po.paidToDate) + currency,
        // This document plus the others being paid with it: the batch is what is checked.
        paying: money(po.payingNow + po.payingWithOthers) + currency,
        shortfall: money(po.shortfall) + currency,
      };
      const line = instant(
        translate,
        'ApDocument_ReceivedShortfall_Line',
        '{{po}}: received {{received}} · paid {{paid}} · paying {{paying}} — {{shortfall}} over',
        params,
      );
      return po.comparedInFunctional
        ? `${line} ${instant(translate, 'ApDocument_ReceivedShortfall_Functional', '(functional currency: a document on this PO is in another currency)', {})}`
        : line;
    });

  const note = instant(
    translate,
    'ApDocument_ReceivedShortfall_Hint',
    'More would be paid than has been received on the purchase order (pre-tax). Check the goods receipts first.',
    {},
  );
  const click = instant(translate, 'ApDocument_ReceivedShortfall_Click', 'Click for the detail by purchase order.', {});
  return [note, ...lines, click].join('\n');
}

function requestKey(requests: PoReceiptCoverageRequest[]): string {
  return requests.map((r) => `${r.documentReference}:${r.amount}:${r.candidate ? 1 : 0}`).join('|');
}

function money(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** translate.instant with an English fallback that is interpolated the same way — so a key missing from
 * the catalogue still reads as a sentence instead of "{{po}}: …". */
function instant(translate: TranslateService, key: string, fallback: string, params: Record<string, string>): string {
  const value: string = translate.instant(key, params);
  if (value !== key) return value;
  return fallback.replace(/\{\{(\w+)\}\}/g, (_match: string, name: string) => params[name] ?? '');
}
